import * as React from "react"
import * as CheckboxPrimitive from "@radix-ui/react-checkbox"
import { Check, Minus } from "lucide-react"

import { cn } from "@/lib/utils"

/*
  TableCheckbox — the 16px selection box used in tables, group rows and lists (kit `.check`):
  navy when checked, a dash when `indeterminate` (some rows of a group / page selected).

    <TableCheckbox checked={allSelected} indeterminate={someSelected} onCheckedChange={toggleAll}
                   aria-label="Select all 52 rows" />

  Props: checked, indeterminate, onCheckedChange(next: boolean), disabled, aria-label, className.
  Clicks never reach a clickable row (the row would otherwise open). The family checkboxes
  (InvCheckbox, SdCheckbox) have the same look and can re-export this one.
*/
const TableCheckbox = React.forwardRef(({ className, indeterminate = false, checked, onCheckedChange, onClick, ...props }, ref) => {
  const state = indeterminate ? "indeterminate" : Boolean(checked)
  return (
    <CheckboxPrimitive.Root
      ref={ref}
      checked={state}
      // Radix turns an indeterminate box into `true` on click: "select the rest".
      onCheckedChange={(next) => onCheckedChange?.(next === true)}
      onClick={(event) => {
        event.stopPropagation()
        onClick?.(event)
      }}
      className={cn(
        "peer grid h-4 w-4 shrink-0 place-items-center rounded-[5px] border-[1.5px] border-[color:var(--ink-4)] bg-surface text-white transition-colors",
        "hover:border-[color:var(--ink-3)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-40",
        "data-[state=checked]:border-brand data-[state=checked]:bg-brand data-[state=indeterminate]:border-brand data-[state=indeterminate]:bg-brand",
        className
      )}
      {...props}
    >
      <CheckboxPrimitive.Indicator>
        {indeterminate
          ? <Minus className="h-3 w-3" strokeWidth={3} aria-hidden="true" />
          : <Check className="h-3 w-3" strokeWidth={3} aria-hidden="true" />}
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  )
})
TableCheckbox.displayName = "TableCheckbox"

export { TableCheckbox }
